import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { User } from '@/features/member/member.type'
import BottomBarInfoButtons from '@/components/atomic/molecules/BottomBarInfoButtons'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { generateTagId } from '@/utils/test-automation'
import { CountryCodes, Locales } from '@/utils/language'
import { NONMEMBER_ID } from '@/common/constants'
import ConfirmPopup from '@/shared/components/confirm-popup'
import { getDownloadLink, postAmplitude, postGtag } from '@/core/lib'
import { useLocales } from '@/shared/hooks/useLocales'
import { HospitalView } from '@/features/hospital/types/hospital.type'
import { createSignInUrl } from '@/shared/utils/url-utils'

const id = generateTagId('hospital', 'page', 'btn', 'login')
const ariaLabelOfLabel = 'button in bottom bar'

const HospitalDetailBottomBarSection = ({
  hospital,
  user,
  onClickConsultation,
}: {
  hospital: HospitalView
  user: User | null
  onClickConsultation: () => void
}) => {
  const { locale } = useLocales()
  if (locale === Locales.JA_JP) {
    return <JapaneseUserBottomBar user={user} hospital={hospital} />
  }

  if (locale === Locales.KO_KR) {
    return <KoreanUserBottomBar user={user} hospital={hospital} onClickConsultation={onClickConsultation} />
  }

  return <DownloadOnlyBottomBar hospital={hospital} />
}

function DownloadOnlyBottomBar({ hospital, children }: { hospital: HospitalView; children?: React.ReactNode }) {
  const { t } = useTranslation()

  const openDownloadLink = () => {
    postGtag('click_download')
    sendAirbridgeEvent(AirbridgeEvents.APP_DOWNLOAD_BUTTON_CLICK, { semanticAttributes: {} })
    postAmplitude('click_download_in_hospital_consultation_button', {
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
    })
    const link = getDownloadLink()
    window.open(link, '_blank')
  }

  return (
    <BottomBarInfoButtons
      buttonProps={[
        {
          label: t('pages.hospitalDetail.downloadApp'),
          ariaLabelOfLabel,
          id,
          onClick: openDownloadLink,
        },
      ]}
      aria-label="consultation buttons"
    >
      {children}
    </BottomBarInfoButtons>
  )
}

function JapaneseUserBottomBar({ hospital, user }: { hospital: HospitalView; user: User | null }) {
  const { countryPathnamePrefix, locale } = useLocales()
  const router = useRouter()
  const { t } = useTranslation()
  const isLoggedInUser = user && user.id !== NONMEMBER_ID

  if (!hospital.activeGeneralConsultation) {
    return <DownloadOnlyBottomBar hospital={hospital} />
  }

  // 도메스틱이 아닌 경우 앱 다운로드
  if (hospital.country !== CountryCodes.JP) {
    return <DownloadOnlyBottomBar hospital={hospital} />
  }

  // 일본에서 전달한 가능 병원을 제외하고는 예약 버튼 노출을 하지 않음.
  if (
    ![
      3203, // HAAB DREAM 뷰티클리닉 도쿄 본원
      4907, // KAGA CLINIC
      7170, // KAUNIS CLINIC【카우니스 클리닉】
    ].includes(hospital.hospitalId)
  ) {
    return <DownloadOnlyBottomBar hospital={hospital} />
  }

  const trackConsultationClick = () => {
    postAmplitude('click_consultation', {
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
      hospital_region: hospital.location.sido,
      is_logged_in: isLoggedInUser,
    })
    // legacy. 추후 click_consultation로 통합합니다. _modn
    postAmplitude('click_consultation_in_hospital_detail', {
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
      hospital_region: hospital.location.sido,
      is_logged_in: isLoggedInUser,
    })
  }
  const moveToCallbackReservation = () => {
    router.push(`/hospitals/${hospital.hospitalId}/callback-reservations`)
  }
  const moveToCallbackReservationAfterLogin = () => {
    router.push(createSignInUrl(`/hospitals/${hospital.hospitalId}/callback-reservations`, locale))
  }

  if (isLoggedInUser) {
    return (
      <BottomBarInfoButtons
        buttonProps={[
          {
            label: t('pages.hospitalDetail.consultationReservation'),
            onClick: () => {
              trackConsultationClick()
              moveToCallbackReservation()
            },
          },
        ]}
      />
    )
  }

  return (
    <BottomBarInfoButtons
      buttonProps={[
        {
          label: t('pages.hospitalDetail.loginAndConsultationReservation'),
          onClick: () => {
            trackConsultationClick()
            moveToCallbackReservationAfterLogin()
          },
        },
      ]}
    />
  )
}

function KoreanUserBottomBar({
  user,
  hospital,
  onClickConsultation,
}: {
  user: User | null
  hospital: HospitalView
  onClickConsultation: () => void
}) {
  const router = useRouter()
  const { t } = useTranslation()
  const { locale } = useLocales()
  const [isLoginConfirmPopupVisible, setIsLoginConfirmPopupVisible] = useState(false)

  const trackConsultationClick = () => {
    postAmplitude('click_consultation', {
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
      hospital_region: hospital.location.sido,
    })
    // legacy. 추후 click_consultation로 통합합니다. _modn
    postAmplitude('click_consultation_in_hospital_detail', {
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
      hospital_region: hospital.location.sido,
    })
  }

  const closeLoginConfirmPopup = () => {
    setIsLoginConfirmPopupVisible(false)
  }

  if (!hospital.activeGeneralConsultation) {
    return (
      <DownloadOnlyBottomBar hospital={hospital}>
        <KoreanConsultationInformationPhrase hospital={hospital} />
      </DownloadOnlyBottomBar>
    )
  }

  const isLoggedInUser = user && user.id !== NONMEMBER_ID
  if (isLoggedInUser) {
    return (
      <BottomBarInfoButtons
        buttonProps={[
          {
            label: t('pages.hospitalDetail.consultationReservation'),
            ariaLabelOfLabel,
            id,
            onClick: () => {
              trackConsultationClick()
              onClickConsultation()
              router.push(`/hospitals/${hospital.hospitalId}/inquiry`)
            },
          },
        ]}
      >
        <KoreanConsultationInformationPhrase hospital={hospital} />
      </BottomBarInfoButtons>
    )
  }
  return (
    <>
      <BottomBarInfoButtons
        buttonProps={[
          {
            label: t('pages.hospitalDetail.loginAndConsultationReservation'),
            ariaLabelOfLabel,
            id,
            onClick: () => {
              trackConsultationClick()
              setIsLoginConfirmPopupVisible(true)
            },
          },
        ]}
        aria-label="consultation buttons"
      >
        <KoreanConsultationInformationPhrase hospital={hospital} />
      </BottomBarInfoButtons>
      <ConfirmPopup
        isVisible={isLoginConfirmPopupVisible}
        text={{ ...t('pages.hospitalDetail.loginConfirmPopup', { returnObjects: true }) }}
        onConfirm={() => {
          router.push(createSignInUrl(`/hospitals/${hospital.hospitalId}/inquiry`, locale))
        }}
        onClickBackground={closeLoginConfirmPopup}
        onCancel={closeLoginConfirmPopup}
      />
    </>
  )
}

const KoreanConsultationInformationPhrase = ({ hospital }: { hospital: HospitalView }) => {
  if (!hospital.statistic.recentConsultationMemberCount) {
    return null
  }

  return (
    <p aria-label="consultation information phrase" className="text-center typo-title4">
      총
      <span aria-label="consultation count" className="text-orange">
        {` ${hospital.statistic.recentConsultationMemberCount}`}
      </span>
      명이 상담신청을 완료했습니다.
    </p>
  )
}

export default HospitalDetailBottomBarSection
